import React, { useState } from "react";
import { Shield, ShieldCheck, ChevronRight } from "lucide-react";

export default function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState("scope");

  const sections = [
    {
      id: "scope",
      title: "1. Scope & Introduction",
      content:
        'Welcome to EduConnectPro, operated by Freshment (a product of GradConnect Innovations Private Limited) ("Company", "we", "us", or "our"). We provide a secure campus recruitment, credentials-locker, and placement intelligence platform bridging academic institutions, students, and active corporate hiring entities.\n\nWe are strongly committed to protecting the privacy, security, and integrity of all personal data collected, stored, and processed within our systems. This Privacy Policy details our operational data pipelines, collection parameters, storage architectures, and compliance with key regulations, including the Digital Personal Data Protection (DPDP) Act of 2023 in India.',
    },
    {
      id: "collection",
      title: "2. Information We Collect",
      content:
        "Our system gathers distinct information dependent on your registration role on the platform:\n\n• Student Roster Parameters: Full name, university-verified CGPA, academic branch, historical backlogs count, contact telephone numbers, institutional email addresses, and uploaded PDF/markdown resumes.\n\n• Institutional Admins & Placement Coordinators: Full names, staff emails, department phone numbers, and official university credentials.\n\n• Corporate Recruiters: Registered business tax identifiers, verified HR emails, recruitment schedules, and detailed criteria specifications.",
    },
    {
      id: "dpdp",
      title: "3. Indian DPDP Act, 2023 Compliance",
      content:
        "In compliance with the Digital Personal Data Protection (DPDP) Act of 2023 (India), GradConnect Innovations Private Limited operates as a Data Fiduciary. Users registering on our platform are recognized as Data Principals.\n\nAll personal data processing is carried out based on explicit, specific, unconditional, unambiguous, and revocable consent. Data Principals have the right to withdraw consent at any time, access summaries of processed personal data, and utilize registered Consent Managers to manage their digital authorizations seamlessly.",
    },
    {
      id: "security-2fa",
      title: "4. 2FA Portal & Security Safeguards",
      content:
        "To guarantee absolute data integrity, EduConnectPro is structured as a mandatory Two-Factor Authentication (2FA) Portal for Security Purposes. Every administrative, recruiter, and student login undergoes strict multi-factor verification to prevent unauthorized access.\n\nIn addition to 2FA, we employ comprehensive security safeguards:\n• TLS 1.3 encryption for data in transit\n• AES-256 protocols for encryption at rest\n• Rigid role-based access control (RBAC)\n• Continuous administrative audit logging\n• Secure localized hosting within Tier-4 data centers",
    },
    {
      id: "usage",
      title: "5. How We Use Collected Data",
      content:
        "We utilize stored data strictly to execute campus recruitment and drive automation tasks in alignment with purpose limitation principles:\n\n• Matching and filtering eligible students against custom recruiter eligibility criteria.\n• Scheduling parallel interview slots and coordinating with university coordinators.\n• Dispatching critical announcements, schedule alerts, and placement metrics via emails or SMS/WhatsApp webhooks.\n• Generating visual dashboards for administrative placement analysis.\n\nWe do NOT lease, rent, monetize, or share student data with third-party marketing entities.",
    },
    {
      id: "disclosure",
      title: "6. Information Sharing & Disclosures",
      content:
        "Student profiles, verified CGPAs, and academic histories are only accessible to corporate recruiters who have been explicitly authorized by the university's placement cell for an active recruitment drive. No public directory of student profiles is indexed or searchable via external engines.\n\nWe may disclose collected information only if required to comply with binding legal subpoenas or government audits under applicable Indian laws.",
    },
    {
      id: "cookies",
      title: "7. Cookie Policy",
      content:
        "Our platform uses cookies to enhance user experience, support secure sessions, and compile performance metrics:\n\n• Essential Cookies: Required for fundamental system operations and user sessions.\n• Security Cookies: Crucial for maintaining and validating 2FA sessions and preventing session hijacking.\n• Analytics & Performance Cookies: Used to observe platform navigation patterns anonymously.\n\nUsers can control and manage cookie preferences through their browser settings, though disabling essential or security cookies may restrict access to secure portal features.",
    },
    {
      id: "principal-rights",
      title: "8. Data Principal Rights (Correction & Erasure)",
      content:
        "Under the DPDP Act of 2023 and global privacy standards, Data Principals possess complete control over their personal records. This includes the right to:\n\n• Seek confirmation and a summary of personal data being processed.\n• Request correction, completion, updating, and absolute erasure of their profiles.\n• Register grievances with our Data Protection Officer or escalate concerns to the Data Protection Board of India.\n\nCorrection requests of verified academic records are processed in coordination with your university registrar.",
    },
    {
      id: "retention",
      title: "9. Data Retention & Residency",
      content:
        "All personal data processed by our platform is securely hosted within local cloud server environments inside the territory of India to satisfy strict data residency requirements.\n\nWe retain personal data only for the duration specified under the academic institution's subscription agreement or as necessary to fulfill the primary purposes of placement coordination. Once a contract concludes or an erasure request is authenticated, we permanently purge or anonymize the data.",
    },
    {
      id: "contact",
      title: "10. Contact Us",
      content:
        "If you have any questions, require further details regarding our DPDP compliance framework, wish to exercise your rights as a Data Principal, or want to connect with our Data Protection Officer, please reach out to us:\n\n• Registered Entity Name: GRADCONNECT INNOVATIONS PRIVATE LIMITED\n• Official Electronic Contact: contact@freshmentapp.com\n• Corporate Address: B-519, RK Iconic Sheetal Park, Puneet Naga Bajrang, Rajkot Bhomeshwar Plot, Rajkot- 360006, Gujarat",
    },
  ];

  const handleScrollTo = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div className="position-relative" style={styles.page}>
      {/* Grid overlay background */}
      <div style={styles.gridOverlay} />

      {/* Background gradients */}
      <div style={styles.glowLeft} />
      <div style={styles.glowRight} />

      {/* Page Header */}
      <div
        className="text-center mx-auto position-relative"
        style={styles.header}
      >
        <span
          className="d-inline-flex font-mono align-items-center"
          style={styles.badge}
        >
          <Shield style={{ width: "16px", height: "16px", color: "#FF5F14" }} />
          DATA REGULATIVE POLICY
        </span>
        <h1 className="font-display" style={styles.h1}>
          Privacy Policy
        </h1>
        <p className="font-mono subtext" style={styles.subtext}>
          EduConnectPro By Freshment (a product of GradConnect Innovations Pvt
          Ltd) • LAST REVISED: JULY 06, 2026
        </p>
      </div>

      <div
        className="mx-auto d-flex flex-xl-row flex-column position-relative"
        style={styles.contentRow}
      >
        {/* Sidebar Navigation */}
        <div className="col-12 col-xl-4" style={styles.sidebar}>
          <h3 className="font-mono" style={styles.sidebarTitle}>
            PRIVACY SECTIONS
          </h3>
          <div style={styles.sidebarLinks}>
            {sections.map((sec) => (
              <button
                key={sec.id}
                type="button"
                onClick={() => handleScrollTo(sec.id)}
                className="title-hover d-flex align-items-center justify-content-between "
                style={{
                  ...styles.sidebarButton,
                  ...(activeSection === sec.id
                    ? styles.sidebarButtonActive
                    : styles.sidebarButtonInactive),
                }}
                onMouseDown={(e) => e.preventDefault()}
                onFocus={(e) => {
                  e.currentTarget.style.outline = "none";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <span style={styles.truncate}>{sec.title}</span>

                <ChevronRight
                  style={{
                    width: "16px",
                    height: "16px",
                    color: activeSection === sec.id ? "#FF5F14" : "#cbd5e1",
                    transform:
                      activeSection === sec.id ? "translateX(2px)" : "none",
                    transition: "all .2s ease",
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Legal Text content */}
        <div
          className="col-12 col-xl-8 position-relative content-card-hover"
          style={styles.contentCard}
        >
          <div className="content-topbar" style={styles.topBar} />

          <div style={styles.sectionsWrapper}>
            {sections.map((sec, idx) => (
              <div
                key={sec.id}
                id={sec.id}
                style={{
                  ...styles.section,
                  ...(idx === sections.length - 1 ? styles.sectionLast : {}),
                }}
              >
                <h2 style={styles.sectionTitle}>
                  <span style={styles.sectionBar} />
                  {sec.title}
                </h2>
                <p style={styles.sectionContent}>{sec.content}</p>
              </div>
            ))}
          </div>
          <style>{`
  .content-topbar {
    transition: background-color 0.3s ease;
  }
  .content-card-hover:hover .content-topbar {
    background-color: #FF5F14 !important;
  }
.title-hover:hover {
  color: oklch(20.8% 0.042 265.755) !important;
  background-color: oklch(98.4% 0.003 247.858) !important;
}
  @media (max-width:480px)
  {
            .subtext{font-size: 9px !important;}
  }

`}</style>

          {/* Secure compliance notice */}
          <div className="d-flex align-items-start" style={styles.noticeBox}>
            <ShieldCheck
              style={{
                width: "24px",
                height: "24px",
                color: "#059669",
                flexShrink: 0,
                marginTop: "2px",
              }}
            />
            <div style={{ fontSize: "0.75rem" }}>
              <h4 className="font-mono" style={styles.noticeTitle}>
                Strict Compliance Shield
              </h4>
              <p style={styles.noticeText}>
                EduConnectPro processes college database elements strictly
                within isolated environments. Our encryption keys, operational
                rules, and access permissions are audited to protect student
                records.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    color: "#0f172a",
    padding: "6rem 1rem",
    overflow: "hidden",
  },
  gridOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(to right, #8080800a 1px, transparent 1px), linear-gradient(to bottom, #8080800a 1px, transparent 1px)",
    backgroundSize: "14px 24px",
    maskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    WebkitMaskImage:
      "radial-gradient(ellipse 60% 50% at 50% 0%, #000 70%, transparent 100%)",
    pointerEvents: "none",
  },
  glowLeft: {
    position: "absolute",
    top: "48px",
    left: "48px",
    width: "24rem",
    height: "24rem",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.05)",
    filter: "blur(110px)",
    pointerEvents: "none",
  },
  glowRight: {
    position: "absolute",
    bottom: "48px",
    right: "48px",
    width: "24rem",
    height: "24rem",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 237, 213, 0.3)",
    filter: "blur(120px)",
    pointerEvents: "none",
  },
  header: {
    maxWidth: "48rem",
    marginBottom: "5rem",
    zIndex: 10,
  },
  badge: {
    gap: "0.375rem",
    padding: "0.375rem 0.875rem",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    border: "1px solid rgba(255, 237, 213, 0.6)",
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontFamily: "monospace",
    marginBottom: "1rem",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  h1: {
    fontSize: "2.75rem",
    fontWeight: 900,
    color: "#020617",
    letterSpacing: "-0.025em",
    marginBottom: "1rem",
    lineHeight: 1.1,
  },
  subtext: {
    fontSize: "11px",
    color: "#FF5F14",
    fontFamily: "monospace",
    fontWeight: 800,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  contentRow: {
    maxWidth: "72rem",
    gap: "2.5rem",
    alignItems: "flex-start",
    zIndex: 10,
    fontFamily: "sans-serif",
  },
  sidebar: {
    padding: "1.5rem",
    borderRadius: "1rem",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    backgroundColor: "#fff",
    position: "sticky",
    top: "6rem",
    boxShadow: "0 20px 25px -5px rgba(241, 245, 249, 0.4)",
  },
  sidebarTitle: {
    fontSize: "10.5px",
    fontFamily: "monospace",
    fontWeight: 700,
    letterSpacing: "0.2em",
    color: "#94a3b8",
    textTransform: "uppercase",
    paddingBottom: "0.75rem",
    borderBottom: "1px solid #f1f5f9",
    marginBottom: "1rem",
  },
  sidebarLinks: {
    display: "flex",
    flexDirection: "column",
    gap: "0.25rem",
    fontSize: "0.75rem",
  },
  sidebarButton: {
    width: "100%",
    textAlign: "left",
    padding: "0.75rem 0.875rem",
    borderRadius: "0.75rem",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    outline: "none",

    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    borderLeft: "3px solid transparent",

    backgroundColor: "transparent",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
  },

  sidebarButtonActive: {
    backgroundColor: "#fff7ed",
    color: "#FF5F14",
    borderLeft: "3px solid #FF5F14",
    paddingLeft: "0.875rem",
    boxShadow: "none",
  },

  sidebarButtonInactive: {
    backgroundColor: "transparent",
    color: "#64748b",
    borderLeft: "3px solid transparent",
    paddingLeft: "0.875rem",
    boxShadow: "none",
  },
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contentCard: {
    backgroundColor: "#fff",
    border: "1px solid rgba(226, 232, 240, 0.8)",
    borderRadius: "1rem",
    padding: "2.5rem",
    boxShadow: "0 20px 25px -5px rgba(241, 245, 249, 0.4)",
    overflow: "hidden",
  },
  topBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    backgroundColor: "rgba(255, 95, 20, 0.1)",
  },
  sectionsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: "2.5rem",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    borderBottom: "1px solid rgba(241, 245, 249, 0.8)",
    paddingBottom: "2rem",
  },
  sectionLast: {
    borderBottom: "none",
    paddingBottom: 0,
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: 700,
    letterSpacing: "-0.025em",
    color: "#0f172a",
    display: "flex",
    alignItems: "center",
    gap: "0.625rem",
  },
  sectionBar: {
    width: "6px",
    height: "24px",
    borderRadius: "4px",
    backgroundColor: "#FF5F14",
    display: "inline-block",
  },
  sectionContent: {
    fontSize: "0.875rem",
    color: "#475569",
    lineHeight: 1.625,
    fontWeight: 600,
    whiteSpace: "pre-line",
  },
  noticeBox: {
    marginTop: "3rem",
    padding: "1.5rem",
    borderRadius: "0.75rem",
    border: "1px solid #a7f3d0",
    backgroundColor: "rgba(236, 253, 245, 0.5)",
    gap: "1rem",
  },
  noticeTitle: {
    fontWeight: 800,
    color: "#022c22",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontFamily: "monospace",
    fontSize: "0.75rem",
  },
  noticeText: {
    color: "#047857",
    marginTop: "0.375rem",
    lineHeight: 1.625,
    fontWeight: 500,
  },
};
